import { useState } from 'react'
import { Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { SearchSelect } from '@/components/composites/search-select'
import { ToneBadge } from '@/components/composites/status-badge'
import { useClientProjectsNav, useCrmDrafts, useDismissCrmDraft, useMergeCrmDraft, type CrmDraft } from './api'
import { productLabel } from './model'

/**
 * GoHighLevel deals with no Client Project yet. A deal that reaches Pilot Discovery Booked lands
 * here as a draft; its AM opens it (the form comes pre-filled from the deal) or says it should
 * never be a project, or merges it into a Client Project already open (an upsell, a second deal for
 * the same account). Opening or merging links the two, and from then the deal is that project's.
 */
export function CrmDraftsPanel({ onOpen }: { onOpen: (draft: CrmDraft) => void }) {
  const drafts = useCrmDrafts(true)
  const dismiss = useDismissCrmDraft()
  const [merging, setMerging] = useState<CrmDraft | null>(null)
  const items = drafts.data?.items ?? []
  if (!items.length) return null
  return (
    <section className="mt-6 grid gap-2 rounded-lg border border-border bg-card p-3" aria-label="From the CRM">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold"><Zap className="size-4 text-brand" /> From the CRM: {items.length} waiting to be opened</h2>
        <p className="text-tiny text-text-muted">Deals in GoHighLevel with no Client Project yet. Opening one starts it in Discovery and lets the deal move it until Closed Won.</p>
      </div>
      <ul className="grid gap-1.5">
        {items.map((d) => (
          <li key={d.dealId} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border-soft px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{d.name}</p>
              <p className="truncate text-tiny text-text-secondary">
                {d.company.name}
                {d.owner.name ? ` · ${d.owner.name}` : ''}
                {d.value ? ` · ${d.value.toLocaleString()}` : ''}
                {d.products.length ? ` · ${d.products.map(productLabel).join(', ')}` : ''}
              </p>
            </div>
            <ToneBadge tone="info" size="xs">{d.stage || 'Sales pipeline'}</ToneBadge>
            <Button size="sm" onClick={() => onOpen(d)}>Open Client Project</Button>
            <Button size="sm" variant="outline" title="This deal belongs to a Client Project that is already open" onClick={() => setMerging(d)}>
              Merge to an existing project
            </Button>
            <Button size="sm" variant="ghost" disabled={dismiss.isPending} title="An individual, a partner, a duplicate: never suggest it again" onClick={() => dismiss.mutate(d.dealId)}>
              Not a project
            </Button>
          </li>
        ))}
      </ul>
      {merging ? <MergeDraftDialog draft={merging} onClose={() => setMerging(null)} /> : null}
    </section>
  )
}

function MergeDraftDialog({ draft, onClose }: { draft: CrmDraft; onClose: () => void }) {
  const options = (useClientProjectsNav(true).data ?? []).map((p) => ({ id: p.id, label: p.label }))
  const [target, setTarget] = useState('')
  const merge = useMergeCrmDraft()
  return (
    <Dialog open onOpenChange={(o) => !o && !merge.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Merge “{draft.name}” into an existing project</DialogTitle>
          <DialogDescription>
            Use this when the deal belongs to a Client Project that is already open, such as an upsell or a second deal for the same account. The deal is linked to the project you pick and stops showing here. A project still in Discovery or Pilot follows the deal; one past them is not moved by it. You can unlink it from that project's page.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1.5">
          <Label>Merge into</Label>
          <SearchSelect value={target} onChange={setTarget} options={options} placeholder="Search Client Projects…" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={merge.isPending}>Cancel</Button>
          <Button disabled={!target || merge.isPending} onClick={() => merge.mutate({ dealId: draft.dealId, projectId: target }, { onSuccess: onClose })}>
            {merge.isPending ? 'Merging…' : 'Merge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
